import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { DateRangePicker, Input, PHONE_OR_TOUCH_QUERY, useMediaQuery } from '@bastion/ui';
import type { ActiveFilter, FilterConfig, FilterState } from '../types';
import css from './FilterMenu.module.css';

type SubPanelSide = 'left' | 'right';

/** The control that takes focus after a panel swap in drill mode. */
type DrillFocus = { to: 'back' } | { to: 'attr'; id: string };

const MAX_RENDERED_OPTIONS = 250;

export interface FilterMenuProps {
  /** Configs for each filterable attribute. Order is preserved in the menu. */
  filterConfigs: FilterConfig[];
  /** Currently active filters; values inside the picker reflect this state. */
  value: FilterState;
  /**
   * Attribute id to open expanded on mount. Useful when re-opening the menu
   * focused on a chip the user just clicked.
   */
  initialAttributeId?: string | null;
  onChange: (next: FilterState) => void;
  /**
   * Called when the user presses Escape inside the search box with no
   * query. The consumer typically closes the surrounding popover.
   */
  onClose: () => void;
}

function defaultOperatorFor(config: FilterConfig): ActiveFilter['operator'] {
  return config.defaultOperator ?? 'is';
}

function upsertFilter(
  state: FilterState,
  nextFilter: ActiveFilter,
): FilterState {
  const idx = state.filters.findIndex((f) => f.attributeId === nextFilter.attributeId);
  if (idx === -1) {
    if (nextFilter.values.length === 0) {
      return state;
    }
    return { filters: [...state.filters, nextFilter] };
  }
  const nextFilters = state.filters.slice();
  if (nextFilter.values.length === 0) {
    nextFilters.splice(idx, 1);
  } else {
    nextFilters[idx] = nextFilter;
  }
  return { filters: nextFilters };
}

function toggleValue(
  state: FilterState,
  attributeId: string,
  defaultOperator: ActiveFilter['operator'],
  optionValue: string,
): FilterState {
  const existing = state.filters.find((f) => f.attributeId === attributeId);
  const base: ActiveFilter = existing ?? {
    attributeId,
    operator: defaultOperator,
    values: [],
  };
  const set = new Set(base.values);
  if (set.has(optionValue)) {
    set.delete(optionValue);
  } else {
    set.add(optionValue);
  }
  const values = Array.from(set);
  return upsertFilter(state, { ...base, values });
}

function activeCountForConfig(
  cfg: FilterConfig,
  active: ActiveFilter | undefined,
  state: FilterState,
): number {
  if (cfg.kind === 'multi-select' && cfg.activeCount) {
    return cfg.activeCount(state, active);
  }
  if (!active || active.values.length === 0) {
    return 0;
  }
  if (cfg.kind === 'multi-select') {
    return active.values.length;
  }
  if (cfg.kind === 'text-search') {
    return active.values[0]?.trim().length ? 1 : 0;
  }
  if (cfg.kind === 'date-range' || cfg.kind === 'numeric-range') {
    const from = active.values[0]?.trim() ?? '';
    const to = active.values[1]?.trim() ?? '';
    return from.length > 0 || to.length > 0 ? 1 : 0;
  }
  return 0;
}

export function FilterMenu({
  filterConfigs,
  value,
  initialAttributeId,
  onChange,
  onClose,
}: FilterMenuProps) {
  const [attrQuery, setAttrQuery] = useState('');
  const [valueQuery, setValueQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(
    initialAttributeId ?? null,
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const attrButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [subPanelSide, setSubPanelSide] = useState<SubPanelSide>('left');
  const [subPanelTop, setSubPanelTop] = useState(0);
  // Drill mode, on a phone or a touch screen: one panel at a time. A tap on
  // an attribute shows its value panel in place of the list, under a back
  // row. Hover and focus do not open a panel. FilterMenu.module.css uses the
  // same query.
  const drill = useMediaQuery(PHONE_OR_TOUCH_QUERY);
  const backRowRef = useRef<HTMLButtonElement>(null);
  const drillFocusRef = useRef<DrillFocus | null>(null);

  // The swap removes the focused row, so move focus to the new panel.
  useLayoutEffect(() => {
    const target = drillFocusRef.current;
    drillFocusRef.current = null;
    if (!drill || !target) return;
    if (target.to === 'back') {
      backRowRef.current?.focus();
    } else {
      attrButtonRefs.current.get(target.id)?.focus();
    }
  }, [drill, expandedId]);

  useLayoutEffect(() => {
    if (drill || !rootRef.current) {
      return undefined;
    }
    const compute = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const viewportWidth = window.innerWidth;
      const SUB_PANEL_MIN = 240;
      const spaceLeft = rect.left;
      const spaceRight = viewportWidth - rect.right;
      if (spaceLeft >= SUB_PANEL_MIN || spaceLeft >= spaceRight) {
        setSubPanelSide('left');
      } else if (spaceRight >= SUB_PANEL_MIN) {
        setSubPanelSide('right');
      }
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [drill, expandedId]);

  const aq = attrQuery.trim().toLowerCase();
  const visibleAttrs = useMemo(() => {
    if (!aq) {
      return filterConfigs;
    }
    return filterConfigs.filter(
      (c) => c.label.toLowerCase().includes(aq) || c.id.toLowerCase().includes(aq),
    );
  }, [filterConfigs, aq]);

  // Align the submenu's `top` with the currently hovered/expanded attribute
  // row. Without this the submenu always pops up at the top of the menu
  // root, so hovering "Collector" (row 7) shows its values panel next to
  // the search box at the top instead of next to Collector itself.
  // Re-measures when the visible list changes (filter typed) since that
  // shifts rows around.
  useLayoutEffect(() => {
    if (!expandedId || !rootRef.current) return;
    const btn = attrButtonRefs.current.get(expandedId);
    if (!btn) return;
    const rootRect = rootRef.current.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    setSubPanelTop(btnRect.top - rootRect.top);
  }, [expandedId, visibleAttrs]);

  useEffect(() => {
    if (expandedId && !filterConfigs.some((c) => c.id === expandedId)) {
      setExpandedId(null);
    }
  }, [filterConfigs, expandedId]);

  useEffect(() => {
    setValueQuery('');
  }, [expandedId]);

  const expandedConfig = useMemo(() => {
    if (!expandedId) {
      return null;
    }
    return filterConfigs.find((c) => c.id === expandedId) ?? null;
  }, [filterConfigs, expandedId]);

  const expandedFilter = useMemo(() => {
    if (!expandedId) {
      return null;
    }
    return value.filters.find((f) => f.attributeId === expandedId) ?? null;
  }, [value.filters, expandedId]);

  const expandedOptions = useMemo(() => {
    if (!expandedConfig || expandedConfig.kind !== 'multi-select') {
      return [];
    }
    const vq = valueQuery.trim().toLowerCase();
    if (!vq) {
      return expandedConfig.options;
    }
    return expandedConfig.options.filter(
      (o) =>
        o.label.toLowerCase().includes(vq) ||
        o.value.toLowerCase().includes(vq),
    );
  }, [expandedConfig, valueQuery]);
  const renderedOptions = useMemo(() => {
    const visible = expandedOptions.slice(0, MAX_RENDERED_OPTIONS);
    if (!expandedConfig || expandedConfig.kind !== 'multi-select') {
      return visible;
    }
    const visibleValues = new Set(visible.map((opt) => opt.value));
    const selectedOverflow = expandedOptions.filter((opt) => {
      if (visibleValues.has(opt.value)) {
        return false;
      }
      return opt.isSelected?.(value) ?? (expandedFilter?.values.includes(opt.value) ?? false);
    });
    return [...selectedOverflow, ...visible];
  }, [expandedConfig, expandedFilter?.values, expandedOptions, value]);
  const hiddenOptionCount = expandedOptions.length - renderedOptions.length;
  // In drill mode the value panel takes the place of the list, so it has no
  // side and no top offset.
  const subPanelClassName = drill
    ? css.subPanel
    : `${css.subPanel} ${subPanelSide === 'right' ? css.subPanelRight : css.subPanelLeft}`;
  const subPanelStyle = drill ? undefined : { top: subPanelTop };

  return (
    <div className={css.root} data-testid="filter-menu" ref={rootRef}>
      {drill && expandedConfig ? (
        <button
          ref={backRowRef}
          type="button"
          className={css.backRow}
          onClick={() => {
            drillFocusRef.current = { to: 'attr', id: expandedConfig.id };
            setExpandedId(null);
          }}
          aria-label={`Back to filters from ${expandedConfig.label}`}
          data-testid="filter-menu-back"
        >
          <ChevronLeft className={css.backIcon} style={{ width: 14, height: 14 }} aria-hidden />
          <span className={css.backLabel}>{expandedConfig.label}</span>
        </button>
      ) : (
        <div className={css.mainPanel}>
          <Input
            className={css.search}
            placeholder="Add filter…"
            value={attrQuery}
            onChange={(e) => setAttrQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                if (attrQuery) {
                  setAttrQuery('');
                  return;
                }
                onClose();
              }
            }}
            autoFocus={!drill}
          />
          <div className={css.attrList} role="menu" data-testid="filter-attribute-list">
            {visibleAttrs.length === 0 ? (
              <div className={css.empty}>No matching filters</div>
            ) : (
              visibleAttrs.map((cfg) => {
                const Icon = cfg.icon;
                const active = value.filters.find((f) => f.attributeId === cfg.id);
                const isExpanded = expandedId === cfg.id;
                const count = activeCountForConfig(cfg, active, value);
                return (
                  <button
                    key={cfg.id}
                    ref={(el) => {
                      if (el) {
                        attrButtonRefs.current.set(cfg.id, el);
                      } else {
                        attrButtonRefs.current.delete(cfg.id);
                      }
                    }}
                    type="button"
                    role="menuitem"
                    className={`${css.attrItem} ${isExpanded ? css.attrItemActive : ''}`}
                    onMouseEnter={drill ? undefined : () => setExpandedId(cfg.id)}
                    onFocus={drill ? undefined : () => setExpandedId(cfg.id)}
                    onClick={() => {
                      if (drill) drillFocusRef.current = { to: 'back' };
                      setExpandedId(cfg.id);
                    }}
                    data-testid={`filter-attr-${cfg.id}`}
                    aria-expanded={isExpanded}
                    aria-haspopup="menu"
                  >
                    {Icon ? (
                      <Icon className={css.attrIcon} style={{ width: 14, height: 14 }} />
                    ) : (
                      <span className={css.attrIconSpacer} />
                    )}
                    <span className={css.attrLabel}>{cfg.label}</span>
                    {count > 0 ? <span className={css.attrCount}>{count}</span> : null}
                    <ChevronRight
                      className={css.attrChevron}
                      style={{ width: 14, height: 14 }}
                      aria-hidden
                    />
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
      {expandedConfig && expandedConfig.kind === 'multi-select' ? (
        <div
          className={subPanelClassName}
          style={subPanelStyle}
          role="menu"
          data-testid="filter-value-submenu"
        >
          <Input
            className={css.search}
            placeholder="Filter…"
            value={valueQuery}
            onChange={(e) => setValueQuery(e.target.value)}
          />
          <div className={css.valueList} role="listbox" aria-multiselectable>
            {expandedConfig.loading ? (
              <div className={css.empty}>Loading values...</div>
            ) : expandedOptions.length === 0 ? (
              <div className={css.empty}>No matching values</div>
            ) : (
              <>
                {renderedOptions.map((opt) => {
                  const selected =
                    opt.isSelected?.(value) ?? (expandedFilter?.values.includes(opt.value) ?? false);
                  const OptIcon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`${css.valueItem} ${selected ? css.valueItemSelected : ''}`}
                      onClick={() => {
                        onChange(
                          opt.onSelect
                            ? opt.onSelect(value)
                            : toggleValue(
                                value,
                                expandedConfig.id,
                                defaultOperatorFor(expandedConfig),
                                opt.value,
                              ),
                        );
                      }}
                    >
                      <span className={css.valueCheck} aria-hidden>
                        {selected ? <Check style={{ width: 14, height: 14 }} /> : null}
                      </span>
                      {OptIcon ? (
                        <OptIcon className={css.valueIcon} style={{ width: 14, height: 14 }} />
                      ) : null}
                      <span
                        className={css.valueLabel}
                        style={opt.indent ? { paddingLeft: opt.indent * 14 } : undefined}
                      >
                        {opt.label}
                      </span>
                      {typeof opt.count === 'number' ? (
                        <span className={css.valueCount}>{opt.count}</span>
                      ) : null}
                    </button>
                  );
                })}
                {hiddenOptionCount > 0 ? (
                  <div className={css.overflowHint}>
                    Showing first {MAX_RENDERED_OPTIONS.toLocaleString()} of{' '}
                    {expandedOptions.length.toLocaleString()}. Search to narrow.
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}
      {expandedConfig && expandedConfig.kind === 'text-search' ? (
        <TextSearchSubPanel
          key={expandedConfig.id}
          config={expandedConfig}
          filter={expandedFilter}
          value={value}
          onChange={onChange}
          subPanelClassName={subPanelClassName}
          subPanelStyle={subPanelStyle}
        />
      ) : null}
      {expandedConfig && expandedConfig.kind === 'date-range' ? (
        <DateRangeSubPanel
          key={expandedConfig.id}
          config={expandedConfig}
          filter={expandedFilter}
          value={value}
          onChange={onChange}
          subPanelClassName={subPanelClassName}
          subPanelStyle={subPanelStyle}
        />
      ) : null}
      {expandedConfig && expandedConfig.kind === 'numeric-range' ? (
        <NumericRangeSubPanel
          key={expandedConfig.id}
          config={expandedConfig}
          filter={expandedFilter}
          value={value}
          onChange={onChange}
          subPanelClassName={subPanelClassName}
          subPanelStyle={subPanelStyle}
        />
      ) : null}
      {expandedConfig && expandedConfig.kind === 'time-range' ? (
        <TimeRangeSubPanel
          key={expandedConfig.id}
          config={expandedConfig}
          filter={expandedFilter}
          value={value}
          onChange={onChange}
          subPanelClassName={subPanelClassName}
          subPanelStyle={subPanelStyle}
        />
      ) : null}
    </div>
  );
}

function TextSearchSubPanel({
  config,
  filter,
  value,
  onChange,
  subPanelClassName,
  subPanelStyle,
}: {
  config: Extract<FilterConfig, { kind: 'text-search' }>;
  filter: ActiveFilter | null;
  value: FilterState;
  onChange: (next: FilterState) => void;
  subPanelClassName: string;
  subPanelStyle?: React.CSSProperties;
}) {
  const committedText = filter?.values[0] ?? '';
  const [draft, setDraft] = useState(committedText);
  const debounceMs = config.debounceMs ?? 300;
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const prevFilterRef = useRef(filter);
  useLayoutEffect(() => {
    if (prevFilterRef.current != null && filter === null) {
      setDraft('');
    }
    prevFilterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    setDraft(committedText);
  }, [committedText]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const trimmed = draft.trim();
      const op = filter?.operator ?? defaultOperatorFor(config);
      const nextFilter: ActiveFilter = {
        attributeId: config.id,
        operator: op,
        values: trimmed.length > 0 ? [trimmed] : [],
      };
      const current =
        valueRef.current.filters.find((f) => f.attributeId === config.id)?.values[0] ?? '';
      if (trimmed === current.trim()) {
        return;
      }
      onChange(upsertFilter(valueRef.current, nextFilter));
    }, debounceMs);
    return () => window.clearTimeout(t);
  }, [config, debounceMs, draft, filter?.operator, onChange]);

  const listId = `${config.id}-suggestions`;
  const renderedSuggestions = config.suggestions?.slice(0, MAX_RENDERED_OPTIONS) ?? [];

  return (
    <div className={subPanelClassName} style={subPanelStyle} data-testid="filter-text-submenu" role="menu">
      <Input
        className={css.search}
        placeholder={config.placeholder ?? 'Search…'}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        list={renderedSuggestions.length ? listId : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
          }
        }}
      />
      {renderedSuggestions.length ? (
        <datalist id={listId}>
          {renderedSuggestions.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </datalist>
      ) : null}
    </div>
  );
}

function DateRangeSubPanel({
  config,
  filter,
  value,
  onChange,
  subPanelClassName,
  subPanelStyle,
}: {
  config: Extract<FilterConfig, { kind: 'date-range' }>;
  filter: ActiveFilter | null;
  value: FilterState;
  onChange: (next: FilterState) => void;
  subPanelClassName: string;
  subPanelStyle?: React.CSSProperties;
}) {
  const fromRaw = filter?.values[0]?.trim() ?? '';
  const toRaw = filter?.values[1]?.trim() ?? '';
  const from = fromRaw.length > 0 ? fromRaw.slice(0, 10) : null;
  const to = toRaw.length > 0 ? toRaw.slice(0, 10) : null;

  return (
    <div className={subPanelClassName} style={subPanelStyle} data-testid="filter-date-submenu" role="menu">
      <DateRangePicker
        from={from}
        to={to}
        onSelect={(range) => {
          const nextFrom = range.from?.trim() ?? '';
          const nextTo = range.to?.trim() ?? '';
          const op = filter?.operator ?? defaultOperatorFor(config);
          const nextFilter: ActiveFilter = {
            attributeId: config.id,
            operator: op,
            values:
              nextFrom.length === 0 && nextTo.length === 0 ? [] : [nextFrom, nextTo],
          };
          onChange(upsertFilter(value, nextFilter));
        }}
        placeholder="Pick a date range"
      />
    </div>
  );
}

function clampNumericString(
  raw: string,
  config: Extract<FilterConfig, { kind: 'numeric-range' }>,
): string {
  const t = raw.trim();
  if (t.length === 0) {
    return '';
  }
  const n = Number(t);
  if (!Number.isFinite(n)) {
    return t;
  }
  let x = n;
  if (config.min !== undefined) {
    x = Math.max(config.min, x);
  }
  if (config.max !== undefined) {
    x = Math.min(config.max, x);
  }
  return String(x);
}

function NumericRangeSubPanel({
  config,
  filter,
  value,
  onChange,
  subPanelClassName,
  subPanelStyle,
}: {
  config: Extract<FilterConfig, { kind: 'numeric-range' }>;
  filter: ActiveFilter | null;
  value: FilterState;
  onChange: (next: FilterState) => void;
  subPanelClassName: string;
  subPanelStyle?: React.CSSProperties;
}) {
  const minWire = filter?.values[0] ?? '';
  const maxWire = filter?.values[1] ?? '';
  const [minDraft, setMinDraft] = useState(minWire);
  const [maxDraft, setMaxDraft] = useState(maxWire);
  const debounceMs = config.debounceMs ?? 300;
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    setMinDraft(minWire);
    setMaxDraft(config.minOnly ? '' : maxWire);
  }, [minWire, maxWire, config.minOnly]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const minT = clampNumericString(minDraft, config);
      const maxT = config.minOnly ? '' : clampNumericString(maxDraft, config);
      const op = filter?.operator ?? defaultOperatorFor(config);
      const nextFilter: ActiveFilter = {
        attributeId: config.id,
        operator: op,
        values: minT.length === 0 && maxT.length === 0 ? [] : [minT, maxT],
      };
      const current = valueRef.current.filters.find((f) => f.attributeId === config.id);
      const curMin = current?.values[0]?.trim() ?? '';
      const curMax = current?.values[1]?.trim() ?? '';
      if (minT === curMin && maxT === curMax) {
        return;
      }
      onChange(upsertFilter(valueRef.current, nextFilter));
    }, debounceMs);
    return () => window.clearTimeout(t);
  }, [config, debounceMs, filter?.operator, maxDraft, minDraft, onChange]);

  const minInput = (
    <label className={css.numericField}>
      <span className={css.numericLabel}>{config.minOnly ? config.label : 'Min'}</span>
      <Input
        type="number"
        className={css.numericInput}
        placeholder={config.placeholder}
        min={config.min}
        max={config.max}
        step={config.step}
        value={minDraft}
        onChange={(e) => setMinDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
          }
        }}
      />
    </label>
  );

  return (
    <div className={subPanelClassName} style={subPanelStyle} data-testid="filter-numeric-submenu" role="menu">
      <div className={css.numericRangeFields}>
        {minInput}
        {!config.minOnly ? (
          <label className={css.numericField}>
            <span className={css.numericLabel}>Max</span>
            <Input
              type="number"
              className={css.numericInput}
              min={config.min}
              max={config.max}
              step={config.step}
              value={maxDraft}
              onChange={(e) => setMaxDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                }
              }}
            />
          </label>
        ) : null}
      </div>
    </div>
  );
}

function isAbsoluteTimeRangeValues(values: string[]): boolean {
  return values.length === 2;
}

function isPresetTimeRangeValues(values: string[]): boolean {
  if (values.length !== 1) return false;
  const v = values[0]?.trim() ?? '';
  return v.length > 0;
}

function TimeRangeSubPanel({
  config,
  filter,
  value,
  onChange,
  subPanelClassName,
  subPanelStyle,
}: {
  config: Extract<FilterConfig, { kind: 'time-range' }>;
  filter: ActiveFilter | null;
  value: FilterState;
  onChange: (next: FilterState) => void;
  subPanelClassName: string;
  subPanelStyle?: React.CSSProperties;
}) {
  const values = filter?.values ?? [];
  const activePresetId = isPresetTimeRangeValues(values) ? values[0] : null;
  const absoluteFrom = isAbsoluteTimeRangeValues(values) ? values[0] : '';
  const absoluteTo = isAbsoluteTimeRangeValues(values) ? values[1] : '';

  const formatLocal = config.formatLocalDateTime ?? ((s: string) => s);
  const parseLocal = config.parseLocalDateTime ?? ((s: string) => s);

  const [fromDraft, setFromDraft] = useState(() => formatLocal(absoluteFrom));
  const [toDraft, setToDraft] = useState(() => formatLocal(absoluteTo));

  useEffect(() => {
    setFromDraft(formatLocal(absoluteFrom));
    setToDraft(formatLocal(absoluteTo));
  }, [absoluteFrom, absoluteTo, formatLocal]);

  const commitAbsolute = (nextFromLocal: string, nextToLocal: string) => {
    const nextFrom = nextFromLocal.length > 0 ? parseLocal(nextFromLocal) : '';
    const nextTo = nextToLocal.length > 0 ? parseLocal(nextToLocal) : '';
    const op = filter?.operator ?? defaultOperatorFor(config);
    const nextFilter: ActiveFilter = {
      attributeId: config.id,
      operator: op,
      values: nextFrom.length === 0 && nextTo.length === 0 ? [] : [nextFrom, nextTo],
    };
    onChange(upsertFilter(value, nextFilter));
  };

  const selectPreset = (presetId: string) => {
    const op = filter?.operator ?? defaultOperatorFor(config);
    const nextFilter: ActiveFilter = {
      attributeId: config.id,
      operator: op,
      values: [presetId],
    };
    onChange(upsertFilter(value, nextFilter));
  };

  const customActive = isAbsoluteTimeRangeValues(values);

  return (
    <div className={subPanelClassName} style={subPanelStyle} data-testid="filter-time-submenu" role="menu">
      <div className={css.valueList} role="listbox">
        {config.presets.map((preset) => {
          const selected = activePresetId === preset.id;
          return (
            <button
              key={preset.id}
              type="button"
              role="option"
              aria-selected={selected}
              className={`${css.valueItem} ${selected ? css.valueItemSelected : ''}`}
              onClick={() => selectPreset(preset.id)}
              data-testid={`filter-time-preset-${preset.id}`}
            >
              <span className={css.valueCheck} aria-hidden>
                {selected ? <Check style={{ width: 14, height: 14 }} /> : null}
              </span>
              <span className={css.valueLabel}>{preset.label}</span>
            </button>
          );
        })}
      </div>
      <div className={css.timeRangeCustom}>
        <div className={css.timeRangeCustomHeader}>
          <span className={css.timeRangeCustomLabel}>Custom range</span>
          {config.timezoneLabel ? (
            <span className={css.timeRangeCustomTz} aria-label={`Timezone: ${config.timezoneLabel}`}>
              {config.timezoneLabel}
            </span>
          ) : null}
          {customActive ? (
            <span className={css.timeRangeCustomActiveDot} aria-hidden />
          ) : null}
        </div>
        <div className={css.timeRangeCustomFields}>
          <label className={css.timeRangeField}>
            <span className={css.timeRangeFieldLabel}>From</span>
            <input
              type="datetime-local"
              className={css.timeRangeInput}
              value={fromDraft}
              onChange={(e) => {
                setFromDraft(e.target.value);
                commitAbsolute(e.target.value, toDraft);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                }
              }}
              data-testid="filter-time-from-input"
            />
          </label>
          <label className={css.timeRangeField}>
            <span className={css.timeRangeFieldLabel}>To</span>
            <input
              type="datetime-local"
              className={css.timeRangeInput}
              value={toDraft}
              onChange={(e) => {
                setToDraft(e.target.value);
                commitAbsolute(fromDraft, e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                }
              }}
              data-testid="filter-time-to-input"
            />
          </label>
        </div>
      </div>
    </div>
  );
}
